import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { apiClient } from '../api';

interface User {
  userId?: string;
  id?: string;
  clerkId: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  suffix?: string;
  vanityUrl?: string;
  vanityUrlActive?: boolean;
  role: string;
  title?: string;
  specialty?: string;
  subspecialty?: string;
  hourlyRate?: number;
  bio?: string;
  credentials?: any[];
  availability?: boolean;
  platformFeeDiscount?: number;
  statesPracticedIn?: string[];
  federalCourtTestified?: boolean;
  emailVerified?: boolean;
  teamOwnerId?: string;
  teamMemberRole?: 'ADMIN' | 'MEMBER';
  doctorFirstName?: string;
  doctorLastName?: string;
  trialStartedAt?: string;
  trialEndsAt?: string;
  trialUsed?: boolean;
  referralCode?: string;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthContextType {
  user: User | null;
  clerkUser: any; // Clerk user object
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>; // Alias for signOut for backwards compatibility
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn } = useUser();
  const { getToken: getClerkToken, signOut: clerkSignOut } = useClerkAuth();
  const [dbUser, setDbUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const fetchingRef = React.useRef(false); // Prevent concurrent fetches
  const lastFetchAttemptRef = React.useRef<number>(0); // Track last fetch time

  // Set up API client with Clerk token getter
  useEffect(() => {
    apiClient.setTokenGetter(async () => {
      try {
        return await getClerkToken();
      } catch (error) {
        console.error('Failed to get Clerk token:', error);
        return null;
      }
    });
  }, [getClerkToken]);

  // Fetch user data from our database when Clerk user is available
  useEffect(() => {
    const fetchUserData = async () => {
      // Prevent concurrent fetches
      if (fetchingRef.current) {
        return;
      }

      // Cooldown period: wait at least 5 seconds between attempts
      const now = Date.now();
      if (now - lastFetchAttemptRef.current < 5000) {
        setIsLoading(false);
        return;
      }

      if (clerkUser && isSignedIn) {
        fetchingRef.current = true;
        lastFetchAttemptRef.current = now;
        try {
          const token = await getClerkToken();

          // Try to get user profile
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3005/api/v1'; // localhost-only fallback
          const response = await fetch(`${apiUrl}/auth/profile`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              setDbUser(data.data);
              setIsLoading(false);
              return;
            }
          } else if (response.status === 404) {
            // User doesn't exist in our database yet, trigger sync
            const syncResponse = await fetch(`${apiUrl}/auth/sync`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (syncResponse.ok) {
              // Retry fetching after sync
              const retryResponse = await fetch(`${apiUrl}/auth/profile`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });

              if (retryResponse.ok) {
                const retryData = await retryResponse.json();
                if (retryData.success && retryData.data) {
                  setDbUser(retryData.data);
                  setIsLoading(false);
                  return;
                }
              }
            } else {
              console.error('Sync failed:', await syncResponse.text());
            }
          }
        } catch (error) {
          console.error('Failed to fetch user data:', error);
        } finally {
          fetchingRef.current = false;
        }
      } else {
        fetchingRef.current = false;
      }
      setIsLoading(false);
    };

    if (clerkLoaded) {
      if (isSignedIn) {
        fetchUserData();
      } else {
        setDbUser(null);
        setIsLoading(false);
      }
    }
  }, [clerkUser, isSignedIn, clerkLoaded, getClerkToken]);

  // Helper function to get token
  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      return await getClerkToken();
    } catch (error) {
      console.error('Failed to get token:', error);
      return null;
    }
  }, [getClerkToken]);

  // Sign out wrapper
  const signOut = useCallback(async (): Promise<void> => {
    try {
      await clerkSignOut({ redirectUrl: '/auth' });
      setDbUser(null);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }, [clerkSignOut]);

  // Construct user object from Clerk data if dbUser is not available
  const user = useMemo(() => {
    if (dbUser) return dbUser;

    // Fallback to Clerk user data
    if (clerkUser) {
      const metadata = clerkUser.unsafeMetadata || {};
      return {
        clerkId: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || '',
        firstName: clerkUser.firstName || '',
        lastName: clerkUser.lastName || '',
        name: `${clerkUser.firstName || ''} ${clerkUser.lastName || ''}`.trim(),
        role: metadata.role || 'CLIENT',
        title: metadata.prefix
          ? `${metadata.prefix} ${clerkUser.firstName} ${clerkUser.lastName} ${metadata.suffix || ''}`.trim()
          : undefined,
        specialty: metadata.specialty,
        vanityUrl: metadata.vanityUrl,
      } as User;
    }

    return null;
  }, [dbUser, clerkUser]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      clerkUser,
      token: null, // Token is fetched on-demand via getToken()
      isAuthenticated: !!(isSignedIn && clerkUser),
      isLoading: !clerkLoaded || isLoading,
      getToken,
      signOut,
      logout: signOut, // Alias for backwards compatibility
    }),
    [user, clerkUser, isSignedIn, clerkLoaded, isLoading, getToken, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
